
import React, { useEffect, useState } from "react";
import { PartFilter } from "@/data/parts";
import RequirementIcon from "./icons/RequirementIcon";
import StarIcon from "./icons/StarIcon";
import SortIndicator from "./icons/SortIndicator";
import CollapsibleIcon from "./icons/CollapsibleIcon";

type SortOption = "none" | "rating-high" | "rating-low";

interface MetricsIconRowProps {
  rating: number;
  sortBy: SortOption;
  onRatingClick?: () => void;
  requiresRoot: boolean;
  requiresWiring?: boolean;
  requiresPrinting?: boolean;
  requiresTools?: boolean;
  requiresPurchase?: boolean;
  subcategory: string;
  filters: PartFilter[];
  onFilterToggle?: (filter: PartFilter) => void;
  activeFilters?: PartFilter[];
  isDesktop?: boolean;
}

const MetricsIconRow = ({
  rating,
  sortBy,
  onRatingClick,
  requiresRoot,
  requiresWiring = false,
  requiresPrinting = false,
  requiresTools = false,
  requiresPurchase = false,
  subcategory,
  filters,
  onFilterToggle,
  activeFilters = [],
  isDesktop = true
}: MetricsIconRowProps) => {
  // State to track window size
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  
  // Update window width on resize with throttling
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Initial check
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Helper function for checking if a filter is active
  const isFilterActive = (filter: PartFilter): boolean => {
    return activeFilters?.includes(filter) || false;
  };
  
  // Get the cursor style based on whether badges are clickable
  const getCursorClass = () => onFilterToggle ? "cursor-pointer" : "cursor-help";
  
  // Handle click on icon
  const handleIconClick = (filter: PartFilter) => {
    if (onFilterToggle) {
      onFilterToggle(filter);
    }
  };
  
  // Check if we need to show various icons
  const hasWiring = requiresWiring || filters.includes("wiring");
  const hasPrinting = requiresPrinting || filters.includes("requires-printing");
  const hasTools = requiresTools || subcategory === "tools" || filters.includes("requires-tools");
  const hasPurchase = requiresPurchase || filters.includes("requires-purchase");
  
  // Adjust icon size based on window width
  const getIconSize = () => {
    if (windowWidth < 320) return 12; // Very small screens
    if (windowWidth < 375) return 13; // Extra small screens
    if (windowWidth < 480) return 14; // Small screens
    if (windowWidth < 640) return 15; // Medium small screens
    return isDesktop ? 16 : 15;  // Default sizes
  };
  
  const iconSize = getIconSize();

  // Generate star components
  const generateStarIcons = () => {
    const stars = [];
    
    // Create individual star components for a more uniform spacing
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <StarIcon 
          key={`star-${i}`} 
          rating={rating} 
          starPosition={i} 
          iconSize={iconSize} 
          onClick={onRatingClick} 
          className={onRatingClick ? "cursor-pointer" : ""}
        />
      );
    }
    
    return stars;
  };

  return (
    <div 
      className="flex items-center justify-evenly w-full px-5 py-1 gap-1 sm:gap-0.5 flex-nowrap"
      aria-label="Part properties and ratings"
    >
      {/* Star rating displayed as individual icons */}
      {generateStarIcons()}
      
      {/* Sort indicator component */}
      <SortIndicator 
        sortBy={sortBy} 
        iconSize={iconSize} 
        onRatingClick={onRatingClick} 
      />
      
      {/* Root icon */}
      <RequirementIcon
        type="root"
        isActive={requiresRoot}
        isFilterActive={isFilterActive("requires-root")}
        iconSize={iconSize}
        getCursorClass={getCursorClass}
        handleIconClick={handleIconClick}
      />

      {/* Tools icon */}
      <RequirementIcon
        type="tools"
        isActive={hasTools}
        isFilterActive={isFilterActive("requires-tools")}
        iconSize={iconSize}
        getCursorClass={getCursorClass}
        handleIconClick={handleIconClick}
      />

      {/* Wiring icon */}
      <RequirementIcon
        type="wiring"
        isActive={hasWiring}
        isFilterActive={isFilterActive("wiring")}
        iconSize={iconSize}
        getCursorClass={getCursorClass}
        handleIconClick={handleIconClick}
      />

      {/* Printed parts icon */}
      <RequirementIcon
        type="printing"
        isActive={hasPrinting}
        isFilterActive={isFilterActive("requires-printing")}
        iconSize={iconSize}
        getCursorClass={getCursorClass}
        handleIconClick={handleIconClick}
      />

      {/* Purchase icon */}
      <RequirementIcon
        type="purchase"
        isActive={hasPurchase}
        isFilterActive={isFilterActive("requires-purchase")}
        iconSize={iconSize}
        getCursorClass={getCursorClass}
        handleIconClick={handleIconClick}
      />
      
      {/* Collapsible chevron - only passing the iconSize prop since the component uses the context internally */}
      <CollapsibleIcon iconSize={iconSize} />
    </div>
  );
};

export default MetricsIconRow;
